// Attribute categories and their values, live, for every surface that reads
// them: the Data sidebar, the Attributes manager, filter and parameter pickers,
// the upload tag selectors, and the table wizard.
//
// Categories are few, so the whole collection is watched for the company.
// Values can be numerous, so a category's values are watched only while at
// least one mounted component asks for them (reference-counted per category)
// and the last list seen stays cached afterwards, so re-opening paints at once.
// The Firestore SDK shares one underlying subscription per identical query, so
// several consumers of the same category cost one stream, not several.
import { isResourceDeleting } from '../utils/resourceLifecycle';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './AuthContext';
import { normalizeCategoryType, toSelectionOption } from '../utils/tagValueUtils';

const AttributesContext = createContext(null);

const EMPTY_LIST = [];
// Every value of a watched category is held in memory and offered in full by
// the pickers. A category this large will be felt, so it is named in the
// console rather than failing quietly.
const LARGE_CATEGORY_WARNING = 10000;

export function AttributesProvider({ children }) {
  const { companyName, currentUserPlatform, authReady } = useAuth();
  const platformUserId = currentUserPlatform?.uid || null;
  // Both Firebase sessions must refer to the same user and the Site handover
  // must be complete. This provider sits outside the route's sign-in gate.
  const canSubscribe = Boolean(companyName && platformUserId && authReady);
  const subscriptionScope = useMemo(() => ({
    companyName,
    platformUserId,
    canSubscribe,
    watches: new Map(),
  }), [
    companyName, platformUserId, canSubscribe,
  ]);
  const [categorySnapshot, setCategorySnapshot] = useState(null);
  const [valuesSnapshot, setValuesSnapshot] = useState(null);

  // Scope the cached data as well as the listeners. A new user or company must
  // see empty state immediately, before effects clean up the old subscriptions.
  const categories = categorySnapshot?.scope === subscriptionScope
    ? categorySnapshot.categories : EMPTY_LIST;
  const categoriesError = categorySnapshot?.scope === subscriptionScope
    ? categorySnapshot.error : null;
  const categoriesLoading = canSubscribe && categorySnapshot?.scope !== subscriptionScope;
  const valuesByCategory = useMemo(() => (
    valuesSnapshot?.scope === subscriptionScope ? valuesSnapshot.categories : {}
  ), [valuesSnapshot, subscriptionScope]);

  useEffect(() => {
    if (!subscriptionScope.canSubscribe) return undefined;
    let active = true;
    const unsubscribe = onSnapshot(
      collection(db, `users/${subscriptionScope.companyName}/tag_categories`),
      (snapshot) => {
        if (!active) return;
        const nextCategories = snapshot.docs.map((categoryDoc) => {
          const data = categoryDoc.data();
          return {
            id: data.name_sanitised || categoryDoc.id,
            ...data,
            type: normalizeCategoryType(data.type),
          };
        });
        setCategorySnapshot({ scope: subscriptionScope, categories: nextCategories, error: null });
      },
      (error) => {
        if (!active) return;
        console.error('Error watching attribute categories:', error);
        setCategorySnapshot({ scope: subscriptionScope, categories: EMPTY_LIST, error });
      },
    );
    return () => {
      active = false;
      unsubscribe();
      // Each scope has its own map: an old cleanup cannot close subscriptions
      // that a child's effect has already opened for the new user or company.
      subscriptionScope.watches.forEach((watch) => watch.unsubscribe());
      subscriptionScope.watches.clear();
    };
  }, [subscriptionScope]);

  // Hold a category's values open. Returns the release for this holder.
  const retainValues = useCallback((categoryId) => {
    if (!subscriptionScope.canSubscribe || !categoryId) return () => {};
    const watches = subscriptionScope.watches;
    let watch = watches.get(categoryId);
    if (watch) {
      watch.count += 1;
    } else {
      watch = { count: 1, unsubscribe: () => {} };
      watches.set(categoryId, watch);
      try {
        watch.unsubscribe = onSnapshot(
          collection(db, `users/${subscriptionScope.companyName}/tag_categories/${categoryId}/values`),
          (snapshot) => {
            if (watches.get(categoryId) !== watch) return;
            if (snapshot.size > LARGE_CATEGORY_WARNING) {
              console.warn(`Attribute category "${categoryId}" has ${snapshot.size} values; the pickers load all of them.`);
            }
            const values = snapshot.docs.map((valueDoc) => ({ id: valueDoc.id, ...valueDoc.data() }));
            setValuesSnapshot((previous) => ({
              scope: subscriptionScope,
              categories: {
                ...(previous?.scope === subscriptionScope ? previous.categories : {}),
                [categoryId]: values,
              },
            }));
          },
          (error) => {
            if (watches.get(categoryId) !== watch) return;
            // An errored listener is terminal. A later holder must open a new
            // listener; releases from old holders must not close that replacement.
            console.error(`Error watching values of ${categoryId}:`, error);
            watches.delete(categoryId);
            setValuesSnapshot((previous) => ({
              scope: subscriptionScope,
              categories: {
                ...(previous?.scope === subscriptionScope ? previous.categories : {}),
                [categoryId]: EMPTY_LIST,
              },
            }));
          },
        );
      } catch (error) {
        watches.delete(categoryId);
        // Invalid category paths fail synchronously, before a listener exists.
        console.error(`Cannot watch values of ${categoryId}:`, error);
        setValuesSnapshot((previous) => ({
          scope: subscriptionScope,
          categories: {
            ...(previous?.scope === subscriptionScope ? previous.categories : {}),
            [categoryId]: EMPTY_LIST,
          },
        }));
      }
    }
    let released = false;
    return () => {
      if (released) return;
      released = true;
      if (watches.get(categoryId) !== watch) return;
      watch.count -= 1;
      if (watch.count <= 0) {
        watch.unsubscribe();
        watches.delete(categoryId);
      }
    };
  }, [subscriptionScope]);

  const value = useMemo(() => ({
    categories,
    categoriesLoading,
    categoriesError,
    valuesByCategory,
    retainValues,
  }), [categories, categoriesLoading, categoriesError, valuesByCategory, retainValues]);

  return (
    <AttributesContext.Provider value={value}>
      {children}
    </AttributesContext.Provider>
  );
}

function useAttributesContext() {
  const context = useContext(AttributesContext);
  if (!context) throw new Error('useAttributes… hooks need an AttributesProvider above them');
  return context;
}

// The live category list: [{ id, name, type, description, key_tag, … }].
export function useAttributeCategories({ includeDeleting = false } = {}) {
  const { categories, categoriesLoading, categoriesError } = useAttributesContext();
  const available = useMemo(() => (
    includeDeleting ? categories : categories.filter((category) => !isResourceDeleting(category))
  ), [categories, includeDeleting]);
  return { categories: available, loading: categoriesLoading, error: categoriesError };
}

const toIdList = (ids) => {
  if (!ids) return [];
  const list = Array.isArray(ids) ? ids : [ids];
  return Array.from(new Set(list.filter(Boolean))).sort();
};

// Live raw value documents for one or more categories, held open while this
// component is mounted with them. Pass a category id or an array of ids; the
// result maps each id to its documents ([{ id, name, value, search_text, … }]),
// or to nothing until the first snapshot for that category has arrived.
export function useCategoryValues(ids) {
  const { valuesByCategory, retainValues } = useAttributesContext();
  const signature = toIdList(ids).join('|');

  useEffect(() => {
    if (!signature) return undefined;
    const releases = signature.split('|').map((categoryId) => retainValues(categoryId));
    return () => { releases.forEach((release) => release()); };
  }, [signature, retainValues]);

  return useMemo(() => {
    const result = {};
    (signature ? signature.split('|') : []).forEach((categoryId) => {
      if (valuesByCategory[categoryId]) result[categoryId] = valuesByCategory[categoryId];
    });
    return result;
  }, [signature, valuesByCategory]);
}

// The same, as picker options (toSelectionOption) — the shape the parameter
// and tag pickers consume. `typeById` supplies a category's type where the
// documents do not carry one. A category without a snapshot yet is ABSENT
// (undefined), distinct from a category with no values ([]): a picker must
// show "loading", not fall back to a free-text field, until its list exists.
export function useAttributeOptions(ids, typeById = null) {
  const raw = useCategoryValues(ids);
  const signature = toIdList(ids).join('|');
  return useMemo(() => {
    const options = {};
    (signature ? signature.split('|') : []).forEach((categoryId) => {
      if (!raw[categoryId]) return;
      const type = typeById?.[categoryId];
      options[categoryId] = raw[categoryId].map((valueDoc) => (
        toSelectionOption(type ? { ...valueDoc, type } : valueDoc, valueDoc.id)
      ));
    });
    return options;
  }, [signature, raw, typeById]);
}
